// FormTemplateForm.js - Formulärbyggare: frågor (envals/skala/fritext) + poängberäkning
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import { isGlobalAdmin } from '../../utils/permissions';
import { SCORING_METHOD_LABELS, QUESTION_TYPE_LABELS, isScorable } from './scoringOptions';
import './FormTemplates.css';

const newId = () => `q_${Math.random().toString(36).slice(2, 8)}`;

const newQuestion = (type = 'single_choice') => ({
  id: newId(),
  label: '',
  type,
  required: true,
  options: type === 'single_choice'
    ? [{ value: 1, label: '' }, { value: 2, label: '' }]
    : [],
  min: type === 'scale' ? 0 : '',
  max: type === 'scale' ? 10 : '',
});

const EMPTY_SCORING = { method: 'none', label: '', reverse: [], exclude: [] };

function FormTemplateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isEditing = !!id;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([newQuestion()]);
  const [scoring, setScoring] = useState(EMPTY_SCORING);
  const [isGlobal, setIsGlobal] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [sentCount, setSentCount] = useState(0);
  const [templateId, setTemplateId] = useState(null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const readOnly = isEditing && !canEdit;

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setErrors([]);
    apiClient.get(`/api/form-templates/${id}`)
      .then(res => {
        const t = res.data;
        setTemplateId(t.template_id);
        setTitle(t.title || '');
        setDescription(t.description || '');
        setQuestions((t.questions || []).map(q => ({
          id: q.id,
          label: q.label || '',
          type: q.type,
          required: q.required !== false,
          options: (q.options || []).map(o => ({ value: o.value, label: o.label || '' })),
          min: q.min ?? '',
          max: q.max ?? '',
        })));
        const sc = t.scoring || {};
        setScoring({
          ...EMPTY_SCORING,
          ...sc,
          method: sc.method || 'none',
          label: sc.label || '',
          reverse: sc.reverse || [],
          exclude: sc.exclude || [],
        });
        setIsGlobal(!!t.is_global);
        setCanEdit(!!t.can_edit);
        setSentCount(t.sent_count || 0);
      })
      .catch(err => {
        console.error('Kunde inte hämta formuläret:', err);
        toast.error(err.response?.data?.msg || 'Kunde inte hämta formuläret');
        navigate('/formularmallar');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  /* ── Frågor ─────────────────────────────────────────────────────────── */
  const updateQuestion = (index, patch) => {
    setQuestions(prev => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  };

  const changeType = (index, type) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== index) return q;
      const next = { ...q, type };
      if (type === 'single_choice' && (!q.options || q.options.length < 2)) {
        next.options = [{ value: 1, label: '' }, { value: 2, label: '' }];
      }
      if (type === 'scale') {
        if (next.min === '' || next.min === undefined) next.min = 0;
        if (next.max === '' || next.max === undefined) next.max = 10;
      }
      return next;
    }));
    // Textfrågor kan inte vara omvända/uteslutna
    if (type === 'text') {
      const qid = questions[index].id;
      setScoring(s => ({
        ...s,
        reverse: s.reverse.filter(x => x !== qid),
        exclude: s.exclude.filter(x => x !== qid),
      }));
    }
  };

  const addQuestion = (type) => setQuestions(prev => [...prev, newQuestion(type)]);

  const removeQuestion = (index) => {
    const qid = questions[index].id;
    setQuestions(prev => prev.filter((_, i) => i !== index));
    setScoring(s => ({
      ...s,
      reverse: s.reverse.filter(x => x !== qid),
      exclude: s.exclude.filter(x => x !== qid),
    }));
  };

  const moveQuestion = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= questions.length) return;
    setQuestions(prev => {
      const copy = [...prev];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      return copy;
    });
  };

  const updateOption = (qIndex, oIndex, patch) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIndex) return q;
      return { ...q, options: q.options.map((o, j) => (j === oIndex ? { ...o, ...patch } : o)) };
    }));
  };

  const addOption = (qIndex) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIndex) return q;
      const numeric = q.options.map(o => Number(o.value)).filter(Number.isFinite);
      const nextValue = numeric.length ? Math.max(...numeric) + 1 : 1;
      return { ...q, options: [...q.options, { value: nextValue, label: '' }] };
    }));
  };

  const removeOption = (qIndex, oIndex) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIndex) return q;
      return { ...q, options: q.options.filter((_, j) => j !== oIndex) };
    }));
  };

  /* ── Poäng ──────────────────────────────────────────────────────────── */
  const toggleInList = (key, qid) => {
    setScoring(s => {
      const list = s[key] || [];
      return { ...s, [key]: list.includes(qid) ? list.filter(x => x !== qid) : [...list, qid] };
    });
  };

  /* ── Spara ──────────────────────────────────────────────────────────── */
  const buildPayload = () => {
    const payloadQuestions = questions.map(q => {
      const out = { id: q.id, label: q.label.trim(), type: q.type, required: !!q.required };
      if (q.type === 'single_choice') {
        out.options = q.options.map(o => ({ value: o.value, label: o.label.trim() }));
      } else if (q.type === 'scale') {
        out.min = q.min === '' ? null : Number(q.min);
        out.max = q.max === '' ? null : Number(q.max);
      }
      return out;
    });
    const method = scoring.method || 'none';
    let payloadScoring = null;
    if (method !== 'none') {
      // Städa: frågor som inte längre finns/är poängbara får inte ligga kvar i
      // reverse/exclude/domains (backend svarar annars 400 utan UI-väg att rätta)
      const scorableIds = new Set(questions.filter(isScorable).map(q => q.id));
      const allIds = new Set(questions.map(q => q.id));
      payloadScoring = {
        method,
        label: scoring.label.trim() || null,
        reverse: (scoring.reverse || []).filter(qid => scorableIds.has(qid)),
        exclude: (scoring.exclude || []).filter(qid => scorableIds.has(qid)),
      };
      if (scoring.domains && typeof scoring.domains === 'object') {
        const domains = {};
        Object.entries(scoring.domains).forEach(([name, qids]) => {
          const kept = (Array.isArray(qids) ? qids : []).filter(qid => allIds.has(qid));
          if (kept.length) domains[name] = kept;
        });
        if (Object.keys(domains).length) payloadScoring.domains = domains;
      }
      if (method === 'eq5d') {
        payloadScoring.dimensions = scoring.dimensions;
        payloadScoring.vas = scoring.vas;
      }
    }
    return {
      title: title.trim(),
      description: description.trim() || null,
      questions: payloadQuestions,
      scoring: payloadScoring,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    const localErrors = [];
    if (!title.trim()) localErrors.push('Titel krävs');
    if (questions.length === 0) localErrors.push('Lägg till minst en fråga');
    questions.forEach((q, i) => {
      if (!q.label.trim()) localErrors.push(`Fråga ${i + 1}: frågetext saknas`);
      if (q.type === 'single_choice') {
        if (q.options.length < 2) localErrors.push(`Fråga ${i + 1}: minst två alternativ`);
        q.options.forEach((o, j) => {
          if (o.value === '' || o.value === null || o.value === undefined) localErrors.push(`Fråga ${i + 1}, alternativ ${j + 1}: värde saknas`);
          if (!o.label.trim()) localErrors.push(`Fråga ${i + 1}, alternativ ${j + 1}: text saknas`);
        });
      }
      if (q.type === 'scale' && (q.min === '' || q.max === '' || Number(q.min) >= Number(q.max))) {
        localErrors.push(`Fråga ${i + 1}: min måste vara mindre än max`);
      }
    });
    if (localErrors.length) {
      setErrors(localErrors);
      toast.error('Formuläret har fel som måste rättas');
      return;
    }

    try {
      setSaving(true);
      setErrors([]);
      const payload = buildPayload();
      if (isEditing) {
        await apiClient.put(`/api/form-templates/${templateId}`, payload);
        toast.success('Formuläret är uppdaterat');
      } else {
        if (isGlobalAdmin(user) && isGlobal) payload.is_global = true;
        await apiClient.post('/api/form-templates', payload);
        toast.success('Formuläret är skapat');
      }
      navigate('/formularmallar');
    } catch (err) {
      console.error('Kunde inte spara formuläret:', err);
      const details = err.response?.data?.details;
      setErrors(details && details.length ? details : [err.response?.data?.msg || 'Kunde inte spara formuläret']);
      toast.error(err.response?.data?.msg || 'Kunde inte spara formuläret');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const name = window.prompt('Namn på det nya egna formuläret:', `${title} (kopia)`);
    if (name === null) return;
    try {
      const resp = await apiClient.post(`/api/form-templates/${templateId}/copy`, { title: name });
      toast.success(`"${resp.data.title}" skapat som eget formulär`);
      navigate(`/formularmallar/edit/${resp.data.template_id}`);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Kunde inte kopiera formuläret');
    }
  };

  if (loading) return <div className="ft-form-container"><p className="ft-muted">Hämtar formuläret…</p></div>;

  const scorable = questions.filter(isScorable);
  const method = scoring.method || 'none';

  return (
    <div className="ft-form-container">
      <div className="ft-form-header">
        <h2>
          <i className="fas fa-clipboard-list"></i>{' '}
          {readOnly ? 'Standardformulär' : (isEditing ? 'Redigera formulär' : 'Skapa eget formulär')}
        </h2>
        <button type="button" className="ft-btn" onClick={() => navigate('/formularmallar')}>
          <i className="fas fa-arrow-left"></i> Till listan
        </button>
      </div>

      {readOnly && (
        <div className="ft-notice">
          <i className="fas fa-lock"></i> Detta är ett validerat standardformulär och kan inte ändras.
          <button type="button" className="ft-btn ft-btn-primary ft-btn-sm" onClick={handleCopy}>
            <i className="fas fa-copy"></i> Kopiera som eget formulär
          </button>
        </div>
      )}
      {!readOnly && isEditing && sentCount > 0 && (
        <div className="ft-notice ft-notice-warn">
          <i className="fas fa-exclamation-triangle"></i> Formuläret har redan skickats {sentCount} gånger.
          Ändringar i frågor påverkar hur tidigare svar visas — lägg hellre till nya frågor än att ändra befintliga.
        </div>
      )}

      {errors.length > 0 && (
        <div className="ft-error-box">
          <strong>Rätta följande:</strong>
          <ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <fieldset disabled={readOnly || saving} className="ft-fieldset">
          {/* ── Grunduppgifter ── */}
          <section className="ft-panel">
            <div className="ft-field">
              <label htmlFor="ft-title">Titel *</label>
              <input id="ft-title" type="text" value={title} onChange={e => setTitle(e.target.value)}
                placeholder="T.ex. Smärtskattning inför besök" maxLength={255} />
            </div>
            <div className="ft-field">
              <label htmlFor="ft-desc">Beskrivning (visas för patienten)</label>
              <textarea id="ft-desc" rows={2} value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Kort instruktion till patienten" maxLength={500} />
            </div>
            {!isEditing && isGlobalAdmin(user) && (
              <label className="ft-check">
                <input type="checkbox" checked={isGlobal} onChange={e => setIsGlobal(e.target.checked)} />
                Globalt standardformulär (synligt för alla kliniker, endast global admin kan ändra)
              </label>
            )}
          </section>

          {/* ── Frågor ── */}
          <section className="ft-panel">
            <h3 className="ft-panel-title"><i className="fas fa-question-circle"></i> Frågor</h3>
            {questions.map((q, index) => (
              <div key={q.id} className="ft-question">
                <div className="ft-question-head">
                  <span className="ft-question-num">{index + 1}</span>
                  <select value={q.type} onChange={e => changeType(index, e.target.value)} className="ft-type-select">
                    {Object.entries(QUESTION_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <label className="ft-check ft-check-inline">
                    <input type="checkbox" checked={!!q.required} onChange={e => updateQuestion(index, { required: e.target.checked })} />
                    Obligatorisk
                  </label>
                  <span className="ft-question-tools">
                    <button type="button" className="ft-icon-btn" title="Flytta upp" disabled={index === 0}
                      onClick={() => moveQuestion(index, -1)}><i className="fas fa-arrow-up"></i></button>
                    <button type="button" className="ft-icon-btn" title="Flytta ned" disabled={index === questions.length - 1}
                      onClick={() => moveQuestion(index, 1)}><i className="fas fa-arrow-down"></i></button>
                    <button type="button" className="ft-icon-btn ft-icon-danger" title="Ta bort fråga"
                      onClick={() => removeQuestion(index)}><i className="fas fa-times"></i></button>
                  </span>
                </div>
                <input type="text" className="ft-question-label" value={q.label}
                  onChange={e => updateQuestion(index, { label: e.target.value })}
                  placeholder="Frågetext som patienten ser" maxLength={500} />

                {q.type === 'single_choice' && (
                  <div className="ft-options">
                    <div className="ft-options-head"><span>Värde</span><span>Alternativtext</span><span></span></div>
                    {q.options.map((o, oIndex) => (
                      <div key={oIndex} className="ft-option-row">
                        <input type="text" className="ft-option-value" value={o.value}
                          onChange={e => updateOption(index, oIndex, { value: e.target.value })} placeholder="1" />
                        <input type="text" className="ft-option-label" value={o.label}
                          onChange={e => updateOption(index, oIndex, { label: e.target.value })} placeholder="T.ex. Inga besvär" />
                        <button type="button" className="ft-icon-btn ft-icon-danger" title="Ta bort alternativ"
                          disabled={q.options.length <= 2} onClick={() => removeOption(index, oIndex)}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                    <button type="button" className="ft-btn ft-btn-sm" onClick={() => addOption(index)}>
                      <i className="fas fa-plus"></i> Lägg till alternativ
                    </button>
                    <p className="ft-hint">Numeriska värden (t.ex. 0–4) gör att frågan kan ingå i poängberäkningen.</p>
                  </div>
                )}

                {q.type === 'scale' && (
                  <div className="ft-scale">
                    <label>Min <input type="number" value={q.min} onChange={e => updateQuestion(index, { min: e.target.value })} /></label>
                    <label>Max <input type="number" value={q.max} onChange={e => updateQuestion(index, { max: e.target.value })} /></label>
                    <span className="ft-hint">Patienten anger ett tal inom intervallet (t.ex. 0–10 eller 0–100).</span>
                  </div>
                )}

                {q.type === 'text' && <p className="ft-hint">Fritextsvar (max 4000 tecken). Räknas inte in i poängen.</p>}
              </div>
            ))}

            <div className="ft-add-row">
              <button type="button" className="ft-btn" onClick={() => addQuestion('single_choice')}>
                <i className="fas fa-plus"></i> Envalsfråga
              </button>
              <button type="button" className="ft-btn" onClick={() => addQuestion('scale')}>
                <i className="fas fa-plus"></i> Skala
              </button>
              <button type="button" className="ft-btn" onClick={() => addQuestion('text')}>
                <i className="fas fa-plus"></i> Fritext
              </button>
            </div>
          </section>

          {/* ── Poängberäkning ── */}
          <section className="ft-panel">
            <h3 className="ft-panel-title"><i className="fas fa-calculator"></i> Poängberäkning</h3>
            <p className="ft-hint">
              Poängen räknas ut när patienten skickar in svaret och visas bara för personalen i patientvyn.
              Patienten ser aldrig poängen.
            </p>
            <div className="ft-field-row">
              <div className="ft-field">
                <label htmlFor="ft-method">Metod</label>
                <select id="ft-method" value={method} onChange={e => setScoring(s => ({ ...s, method: e.target.value }))}>
                  {Object.entries(SCORING_METHOD_LABELS)
                    .filter(([v]) => v !== 'eq5d' || method === 'eq5d' || Array.isArray(scoring.dimensions))
                    .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              {method !== 'none' && (
                <div className="ft-field">
                  <label htmlFor="ft-score-label">Etikett (visas vid poängen)</label>
                  <input id="ft-score-label" type="text" value={scoring.label} maxLength={100}
                    onChange={e => setScoring(s => ({ ...s, label: e.target.value }))} placeholder="T.ex. Totalpoäng" />
                </div>
              )}
            </div>

            {method === 'eq5d' && (
              <p className="ft-hint">
                EQ-5D: visar VAS och den deskriptiva hälsoprofilen (t.ex. 21121). Det officiella indexet
                (value-set) beräknas inte i denna version.
              </p>
            )}

            {method !== 'none' && method !== 'eq5d' && (
              <div className="ft-scoring-table">
                {scorable.length === 0 ? (
                  <p className="ft-hint ft-warn">
                    Ingen fråga kan poängsättas ännu — ge envalsfrågorna numeriska värden eller lägg till en skala.
                  </p>
                ) : (
                  <table>
                    <thead>
                      <tr><th>Fråga</th><th>Intervall</th><th>Omvänd</th><th>Räknas ej</th></tr>
                    </thead>
                    <tbody>
                      {scorable.map(q => {
                        const vals = q.type === 'scale'
                          ? [Number(q.min), Number(q.max)]
                          : q.options.filter(o => o.value !== '').map(o => Number(o.value)).filter(Number.isFinite);
                        const lo = Math.min(...vals);
                        const hi = Math.max(...vals);
                        const excluded = scoring.exclude.includes(q.id);
                        return (
                          <tr key={q.id} className={excluded ? 'ft-row-excluded' : ''}>
                            <td>{q.label || <em className="ft-muted">(utan text)</em>}</td>
                            <td>{lo}–{hi}</td>
                            <td>
                              <input type="checkbox" checked={scoring.reverse.includes(q.id)} disabled={excluded}
                                onChange={() => toggleInList('reverse', q.id)} title="Omvänd poäng: värde → max + min − värde" />
                            </td>
                            <td>
                              <input type="checkbox" checked={excluded} onChange={() => toggleInList('exclude', q.id)} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <p className="ft-hint">
                  {method === 'sum' && 'Summa av alla besvarade poängbara frågor (plus procent av max).'}
                  {method === 'mean' && 'Medelvärde av alla besvarade poängbara frågor.'}
                  {method === 'percent_of_max' && 'Summa delat med maxpoängen för de besvarade frågorna, i procent (som t.ex. ODI).'}
                  {' '}Omvänd = frågan räknas spegelvänt inom sitt intervall.
                </p>
              </div>
            )}
          </section>

          {!readOnly && (
            <div className="ft-form-actions">
              <button type="button" className="ft-btn" onClick={() => navigate('/formularmallar')}>Avbryt</button>
              <button type="submit" className="ft-btn ft-btn-primary" disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin"></i> Sparar…</> : (isEditing ? 'Spara ändringar' : 'Skapa formulär')}
              </button>
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}

export default FormTemplateForm;
